import { useCallback } from "react"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import { toast } from "react-toastify"
import * as yup from "yup"
import YupPassword from "yup-password"
import { useAuthControllerResetPasswordMutation } from "~/ui-rtk/api/authApi"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { isUnauthorizedError } from "~/ui-rtk/utils/http-utils"

YupPassword(yup)

export type ChangePasswordFormValues = {
  newPassword: string
  confirmNewPassword: string
}

const initialValues: ChangePasswordFormValues = {
  newPassword: "",
  confirmNewPassword: "",
}

export const validationSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required("New password is required!")
    .min(8, "Must be at least 8 characters long")
    .minLowercase(1, "Must contain a lowercase letter (a-z)")
    .minUppercase(1, "Must contain an uppercase letter (A-Z)")
    .minNumbers(1, "Must contain a number")
    .minSymbols(1, "Must contain a symbol (e.g. @, $, !, %, *, ?)"),
  confirmNewPassword: yup
    .string()
    .required("Must match")
    .oneOf([yup.ref("newPassword"), ""], "Must match"),
})

export const useConnect = (token: string) => {
  const navigate = useStableNavigate()
  const [resetPasswordAsync] = useAuthControllerResetPasswordMutation()

  const redirect = () => {
    setTimeout(() => {
      void navigate(brandRoutes.SIGN_IN)
    }, 1000)
  }

  const submit = useCallback(
    async (values: ChangePasswordFormValues) => {
      try {
        await resetPasswordAsync({
          resetPasswordDto: {
            token,
            newPassword: values.newPassword,
          },
        }).unwrap()
        toast.success("Password was reset successfully. Redirecting...")
        redirect()
      } catch (error: unknown) {
        console.error(error)
        if (isUnauthorizedError(error)) {
          navigate("/")
          return
        }
        toast.error("Could not reset password", {
          toastId: "reset-password-error",
        })
      }
    },
    [token],
  )

  return { initialValues, validationSchema, resetPassword: submit }
}
